/**
 * Thin client for the `did` edge function. Field names mirror the D-ID
 * REST API where possible (`presenter_id`, `voice_id`, ...) so nothing
 * has to be translated at the boundary.
 */
import { createLogger } from "@/lib/frontend-logger";
import { CORRELATION_HEADER, nextCorrelationId } from "@/lib/correlation";
import { appendOp } from "@/lib/opSlug";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
const ENDPOINT = `${SUPABASE_URL}/functions/v1/did`;

const logger = createLogger("did.client");

async function call<T>(action: string, params: Record<string, unknown> = {}): Promise<T> {
  const correlationId = nextCorrelationId();
  const startedAt = performance.now();
  const res = await fetch(appendOp(ENDPOINT, `did-${action.replace(/_/g, "-")}`), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: SUPABASE_KEY,
      authorization: `Bearer ${SUPABASE_KEY}`,
      [CORRELATION_HEADER]: correlationId,
    },
    body: JSON.stringify({ action, ...params }),
  });
  const durationMs = Math.round(performance.now() - startedAt);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = (data && (data.error || data.message)) || `did ${action} failed (${res.status})`;
    logger.error("did call failed", { action, status: res.status, correlationId, durationMs, error: msg });
    throw new Error(msg);
  }
  logger.info("did call ok", { action, status: res.status, correlationId, durationMs });
  return data as T;
}

/** A D-ID voice-capable expressive avatar from `/expressives/avatars`. */
export type DidPresenter = {
  presenter_id: string;
  presenter_type?: "expressive";
  name?: string;
  gender?: string;
  thumbnail_url?: string;
  preview_url?: string;
  image_url?: string;
  video_url?: string;
  talking_preview_url?: string;
  [k: string]: unknown;
};

/** A D-ID TTS voice from `/tts/voices`. */
export type DidVoice = {
  id: string;
  name?: string;
  gender?: string;
  provider?: string;
  language?: string;
  access?: string;
  languages?: Array<{ language?: string; locale?: string; accent?: string; previewUrl?: string }>;
  [k: string]: unknown;
};

/**
 * The parts of a D-ID agent we read back. `presenter.voice` reflects what
 * D-ID actually stored, which is the value we persist on the scenario.
 */
export type DidAgentResponse = {
  id?: string;
  status?: string;
  assets?: Array<{ id?: string; status?: string; [k: string]: unknown }>;
  presenter?: {
    presenter_id?: string;
    background?: { source_url?: string; color?: string };
    voice?: { type?: string; voice_id?: string; language?: string };
    [k: string]: unknown;
  };
  [k: string]: unknown;
};

const READY_POLL_INTERVAL_MS = 2_000;
const READY_TIMEOUT_MS = 45_000;

/**
 * D-ID's agent schema (`AgentResponseDto`) documents no top-level `status`;
 * the only documented status enum in that reference is `AssetStatus`
 * (`created | started | done | error`) on nested `assets[]`. Live agents do
 * return a top-level status drawn from that same vocabulary — `started` for a
 * usable agent — so the values are registered here explicitly. Nothing outside
 * this table is assumed: an unregistered value stops the wait and is reported.
 */
const AGENT_STATUS_READY = new Set(["started", "done"]);
const AGENT_STATUS_PENDING = new Set(["created"]);
const AGENT_STATUS_ERROR = new Set(["error"]);

/** `assets[].status` is the documented signal; an asset error is terminal. */
function failedAsset(agent?: DidAgentResponse) {
  return (agent?.assets ?? []).find(
    (a) => String(a?.status || "").toLowerCase() === "error",
  );
}

/** Wait until D-ID has finished rebuilding an agent after a presenter change. */
export async function waitForDidAgentReady(
  agentId: string,
  timeoutMs = READY_TIMEOUT_MS,
): Promise<DidAgentResponse> {
  const deadline = Date.now() + timeoutMs;
  let lastStatus = "";
  while (Date.now() < deadline) {
    const { agent } = await did.getAgent(agentId);
    lastStatus = String(agent?.status ?? "").trim().toLowerCase();
    const badAsset = failedAsset(agent);
    if (badAsset) {
      logger.error("did agent asset failed", { agentId, assetId: badAsset.id });
      throw new Error("D-ID could not prepare this agent (an asset failed to process).");
    }
    if (AGENT_STATUS_READY.has(lastStatus)) return agent;
    if (AGENT_STATUS_ERROR.has(lastStatus)) {
      throw new Error(`D-ID could not prepare this agent (${lastStatus}).`);
    }
    if (!AGENT_STATUS_PENDING.has(lastStatus)) {
      logger.error("did agent status unregistered", { agentId, status: lastStatus });
      throw new Error(`D-ID returned an unexpected agent status ("${lastStatus}").`);
    }
    await new Promise((resolve) => window.setTimeout(resolve, READY_POLL_INTERVAL_MS));
  }
  throw new Error(`D-ID is still preparing this agent (${lastStatus}). Try again shortly.`);
}

export const did = {
  listPresenters: () => call<{ presenters: DidPresenter[] }>("list_presenters"),
  listVoices: () => call<{ voices: DidVoice[] }>("list_voices"),
  createAgent: (input: {
    name?: string;
    presenter_id: string;
    presenter_type?: "expressive";
    voice_id?: string;
    /** Background id from the local registry; `"none"` clears it. */
    background_id?: string;
    /** Absolute https URL for that background, resolved by the caller. */
    background_url?: string;
    /** Scenario locale (`en-US`). The edge function resolves the provider
     *  and the real language string from the live voice catalog. */
    voice_locale?: string;
    system_prompt: string;
    persona?: string;
    knowledge?: string;
    instructions?: string;
    opening_text?: string;
    /** Scenario-owned knowledge base to attach for retrieval. */
    knowledge_id?: string;
    /** Answer strictly from the knowledge base. */
    knowledge_grounded?: boolean;
    /** Tool ids attached to the agent (`tools.items`). */
    tool_ids?: string[];
  }) => call<{ agent_id: string; agent: DidAgentResponse }>("create_agent", input),
  updateAgent: (input: {
    agent_id: string;
    name?: string;
    presenter_id?: string;
    presenter_type?: "expressive";
    voice_id?: string;
    background_id?: string;
    background_url?: string;
    voice_locale?: string;
    system_prompt?: string;
    persona?: string;
    knowledge?: string;
    instructions?: string;
    opening_text?: string;
    knowledge_id?: string;
    knowledge_grounded?: boolean;
    tool_ids?: string[];
  }) => call<{ agent_id: string; agent: DidAgentResponse }>("update_agent", input),
  getAgent: (agent_id: string) =>
    call<{ agent_id: string; agent: DidAgentResponse }>("get_agent", { agent_id }),
  deleteAgent: (agent_id: string) =>
    call<{ ok: true }>("delete_agent", { agent_id }),
  createClientKey: (agent_id: string) =>
    call<{ client_key: string }>("create_client_key", { agent_id }),
  /**
   * Store a trainer-uploaded background in D-ID's image store and return the
   * hosted URL. Used instead of serving from our own origin, which is not
   * guaranteed to be publicly fetchable.
   */
  uploadBackground: (input: { data_url: string; filename?: string }) =>
    call<{ url: string; id?: string | null }>("upload_background", input),

  /** Create the scenario's knowledge base (`knl_...`). */
  createKnowledge: (input: { name: string; description?: string }) =>
    call<{ knowledge_id: string }>("create_knowledge", input),
  /** Add one fetchable source to the knowledge base. */
  addKnowledgeDocument: (input: {
    knowledge_id: string;
    source_url: string;
    title?: string;
  }) =>
    call<{
      document_id: string;
      title?: string;
      status: "processing" | "ready" | "error";
      error_message?: string;
    }>("add_knowledge_document", input),
  /** Live indexing status; `missing: true` when D-ID no longer has it. */
  getKnowledgeDocument: (input: { knowledge_id: string; document_id: string }) =>
    call<{
      document_id: string;
      title?: string;
      status?: "processing" | "ready" | "error";
      error_message?: string;
      missing?: boolean;
    }>("get_knowledge_document", input),
  deleteKnowledgeDocument: (input: { knowledge_id: string; document_id: string }) =>
    call<{ ok: true }>("delete_knowledge_document", input),
  deleteKnowledge: (knowledge_id: string) =>
    call<{ ok: true }>("delete_knowledge", { knowledge_id }),

  // ── Sessions (vendor-side metadata) ─────────────────────────
  /**
   * Vendor record of one session. D-ID identifies a session by its chat id
   * (`cht_...`), which the SDK hands us through `onNewChat`.
   * `session: null` means D-ID has no record of it (404).
   */
  getSession: (session_id: string) =>
    call<{ session_id: string; session: DidSession | null; missing?: boolean }>(
      "get_session",
      { session_id },
    ),
  listSessions: (input: {
    agent_id?: string;
    from?: string;
    to?: string;
    limit?: number;
    token?: string;
  } = {}) =>
    call<{ sessions: DidSession[]; token: string | null }>("list_sessions", input),

  // ── Chat log export (durable transcript backstop) ───────────
  /** `export_id: null` with `empty: true` when the range holds no chats. */
  createChatExport: (input: { agent_id: string; from?: string; to?: string }) =>
    call<{ export_id: string | null; empty?: boolean; reason?: string }>(
      "create_chat_export",
      input,
    ),
  /** `chats: null` while the export is still running. */
  getChatExport: (export_id: string) =>
    call<{
      export_id: string;
      status: string;
      chats: DidExportedChat[] | null;
      error?: string | null;
    }>("get_chat_export", { export_id }),

  // ── Tools (agent function calling) ──────────────────────────
  listTools: () => call<{ tools: unknown[] }>("list_tools"),
  getTool: (tool_id: string) => call<{ tool_id: string; tool: unknown }>("get_tool", { tool_id }),
  createTool: (tool: Record<string, unknown>) =>
    call<{ tool_id: string | null; tool: unknown }>("create_tool", { tool }),
  updateTool: (input: { tool_id: string; tool: Record<string, unknown> }) =>
    call<{ tool_id: string; tool: unknown }>("update_tool", input),
  deleteTool: (tool_id: string) => call<{ ok: true }>("delete_tool", { tool_id }),
};

/** Session summary/detail from `/v2/agents/sessions`. */
export type DidSession = {
  id: string;
  agent_id?: string;
  user_id?: string;
  start_time?: string;
  duration_minutes?: number;
  message_count?: number;
  region?: string;
  rating?: number;
  recording_status?: "ready" | "failed";
  summary?: string;
  insights?: {
    headline?: string;
    outcome?: string;
    visual_analysis?: string;
    exit_status?: string;
  };
  participant?: Record<string, unknown>;
  [k: string]: unknown;
};

/** One chat inside a D-ID chat-log export archive. */
export type DidExportedChat = {
  /** Matches the `cht_...` id the SDK reports through `onNewChat`. */
  chatId?: string;
  agentId?: string;
  createdAt?: string;
  messages?: Array<{
    role?: string;
    content?: string;
    created_at?: string;
    id?: string;
    [k: string]: unknown;
  }>;
  [k: string]: unknown;
};
